import { IntlShape } from "react-intl";
import { TableColumnsType, TablePaginationConfig } from "antd";
import { IUserData } from "@/data/api/auth-data";
import { BaseOptionType } from "antd/es/select";
import { UserWithPercentageWithKey } from "@/data/api/fee-plans-get-all";
import {
  IDomainConnect,
  IDomainFoundBlock,
  IDomainPayout,
  IDomainPoolHistoryStat,
  IDomainPoolHistoryUserStat,
  IDomainUser,
  IDomainUserStatWorker,
} from "@/data/domain/data";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LineChartOutlined,
  MinusCircleOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import {
  formatShareRateValue,
  getCurrentTimeTimestamp,
} from "@/data-converters/metrics-pipe";
import {
  formatUnixDateEnglish,
  formatUnixDateTimeEnglish,
} from "@/app/date-format";

const naturalTextCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const PAGINATION_THRESHHOLD = 50;
const FOUND_BLOCKS_LAYOUT_GUTTER = 24;
const FOUND_BLOCKS_HASH_MIN_SUFFIX_LENGTH = 8;
const FOUND_BLOCKS_HASH_PREFIX = "...";
const FOUND_BLOCKS_HASH_CHAR_WIDTH_PX = 8;
const FOUND_BLOCKS_HASH_CELL_PADDING_PX = 28;
const FOUND_BLOCKS_HASH_LINK_ADORNMENT_WIDTH_PX = 18;
const FOUND_BLOCKS_FOUND_TEXT_CHAR_WIDTH_PX = 7.6;
const FOUND_BLOCKS_FOUND_CELL_PADDING_PX = 24;

export interface IFoundBlocksTableLayout {
  columns: TableColumnsType<IDomainFoundBlock>;
  tableWidth: number;
  scrollX: number;
}

export function provideTablePaginationBottomLeftAndTopRight(
  length: number | undefined
): false | TablePaginationConfig {
  if (length == undefined || length <= PAGINATION_THRESHHOLD) return false;
  else
    return {
      size: "small",
      pageSize: 50,
      position: ["bottomLeft", "topRight"],
      showSizeChanger: false,
    };
}

// export function provideTablePaginationWhenTotalUndefined(
//   length: number | undefined
// ): false | TablePaginationConfig {
//   if (length == undefined || length <= PAGINATION_THRESHHOLD) return false;
//   else
//     return {
//       size: "small",
//       pageSize: 50,
//       position: ["bottomLeft", "topRight"],
//       showSizeChanger: false,
//       total: undefined
//     };
// }

export function provideTablePaginationUsers(
  length: number | undefined
): false | TablePaginationConfig {
  if (length == undefined || length <= PAGINATION_THRESHHOLD) return false;
  else
    return {
      size: "small",
      pageSizeOptions: [30, 50, 100, 250, 500],
      defaultPageSize: 30,
      pageSize: 30,
      position: ["bottomLeft", "topRight"],
      showSizeChanger: true,
    };
}

export function provideDataColumns(
  intl: IntlShape,
  windowWidth: number,
  containerWidth: number,
  blocks: IDomainFoundBlock[] | undefined,
  options?: {
    resolveHashLink?: (record: IDomainFoundBlock) => string | undefined;
  }
): IFoundBlocksTableLayout {
  const result: TableColumnsType<IDomainFoundBlock> = [];
  const isMobile = windowWidth <= 768;
  const hasHashLinks = options?.resolveHashLink != undefined;
  const safeContainerWidth =
    containerWidth > 0 ? Math.min(containerWidth, windowWidth) : windowWidth;
  const availableWidth =
    safeContainerWidth - FOUND_BLOCKS_LAYOUT_GUTTER;
  const heightWidth = resolveFoundBlocksSingleLineColumnWidth({
    blocks,
    isMobile,
    headerLabel: intl.messages["column-height"] as string,
    valueGetter: (block) => String(block.height),
    minWidth: isMobile ? 48 : 56,
  });
  const ageWidth = resolveFoundBlocksSingleLineColumnWidth({
    blocks,
    isMobile,
    headerLabel: intl.messages["column-age"] as string,
    valueGetter: (block) => block.confirmations,
    minWidth: isMobile ? 48 : 56,
  });
  const rewardWidth = resolveFoundBlocksSingleLineColumnWidth({
    blocks,
    isMobile,
    headerLabel: intl.messages["column-reward"] as string,
    valueGetter: (block) => block.generatedCoins,
    minWidth: isMobile ? 64 : 74,
  });
  const foundWidth = isMobile ? 92 : 108;
  const foundMaxCharsPerLine = resolveFoundBlocksFoundMaxCharsPerLine(foundWidth);
  const dateWidth = resolveFoundBlocksDateColumnWidth({
    blocks,
    isMobile,
    headerLabel: intl.messages["column-date"] as string,
  });
  const hashMinWidth =
    FOUND_BLOCKS_HASH_CELL_PADDING_PX +
    (hasHashLinks ? FOUND_BLOCKS_HASH_LINK_ADORNMENT_WIDTH_PX : 0) +
    (FOUND_BLOCKS_HASH_PREFIX.length + FOUND_BLOCKS_HASH_MIN_SUFFIX_LENGTH) *
      FOUND_BLOCKS_HASH_CHAR_WIDTH_PX;
  const fixedWidthWithFound =
    heightWidth + ageWidth + rewardWidth + foundWidth + dateWidth;
  const fixedWidthWithoutFound =
    heightWidth + ageWidth + rewardWidth + dateWidth;
  const hideFound = availableWidth < fixedWidthWithFound + hashMinWidth;
  const needsScroll = availableWidth < fixedWidthWithoutFound + hashMinWidth;
  const hashColumnWidth = needsScroll
    ? hashMinWidth
    : Math.max(
        hashMinWidth,
        availableWidth - (hideFound ? fixedWidthWithoutFound : fixedWidthWithFound)
      );
  const hashVisibleChars = resolveFoundBlockHashVisibleChars(
    hashColumnWidth,
    hasHashLinks ? FOUND_BLOCKS_HASH_LINK_ADORNMENT_WIDTH_PX : 0
  );
  const tableWidth =
    (hideFound ? fixedWidthWithoutFound : fixedWidthWithFound) + hashColumnWidth;
  const scrollX = needsScroll ? tableWidth : tableWidth;

  result.push({
    title: renderCenteredTableHeader(intl.messages["column-height"] as string),
    dataIndex: "height",
    key: "height",
    width: heightWidth,
    align: "center",
    ellipsis: { showTitle: false },
  });
  result.push({
    title: renderCenteredTableHeader(intl.messages["column-hash"] as string),
    dataIndex: "hash",
    key: "hash",
    width: hashColumnWidth,
    align: "center",
    render: (text, record) => {
      const hash = text as unknown as string;
      const formattedHash = formatFoundBlockHash(hash, hashVisibleChars);
      const hashLink = options?.resolveHashLink?.(record);
      const sharedStyle = {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.28rem",
        maxWidth: "100%",
        whiteSpace: "nowrap" as const,
        overflow: "hidden" as const,
        textOverflow: "clip" as const,
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      };
      const content = (
        <>
          <span>{formattedHash}</span>
          {hashLink ? (
            <ExportOutlined
              style={{
                flexShrink: 0,
                fontSize: "0.8em",
                opacity: 0.72,
                color: "var(--color-primary)",
              }}
            />
          ) : null}
        </>
      );

      if (!hashLink) {
        return (
          <span title={hash} style={sharedStyle}>
            {content}
          </span>
        );
      }

      return (
        <a
          href={hashLink}
          target="_blank"
          rel="noreferrer noopener"
          title={hash}
          onClick={(event) => event.stopPropagation()}
          style={{
            ...sharedStyle,
            color: "inherit",
            textDecoration: "none",
          }}
        >
          {content}
        </a>
      );
    },
  });
  result.push({
    title: renderCenteredTableHeader(intl.messages["column-age"] as string),
    dataIndex: "confirmations",
    key: "confirmations",
    width: ageWidth,
    align: "center",
    ellipsis: { showTitle: false },
  });
  result.push({
    title: renderCenteredTableHeader(intl.messages["column-reward"] as string),
    dataIndex: "generatedCoins",
    key: "generatedCoins",
    width: rewardWidth,
    ellipsis: { showTitle: false },
  });
  if (!hideFound) {
    result.push({
      title: renderCenteredTableHeader(intl.messages["column-found"] as string),
      dataIndex: "foundBy",
      key: "foundBy",
      width: foundWidth,
      ellipsis: false,
      render: (text) => {
        const foundBy = text as unknown as string;
        const lines = formatFoundBlocksFoundBy(
          foundBy,
          foundMaxCharsPerLine
        );

        return (
          <span
            title={foundBy}
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "flex-start",
              maxWidth: "100%",
              lineHeight: 1.15,
            }}
          >
            {lines.map((line, index) => (
              <span
                key={`${foundBy}-${index}`}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "clip",
                }}
              >
                {line}
              </span>
            ))}
          </span>
        );
      },
    });
  }
  result.push({
    title: renderCenteredTableHeader(intl.messages["column-date"] as string),
    dataIndex: "time",
    key: "time",
    width: dateWidth,
    ellipsis: { showTitle: false },
    render: (_, record) => (
      <span
        title={record.time}
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "flex-start",
          lineHeight: 1.15,
          whiteSpace: "nowrap",
        }}
      >
        <span>{record.timeOfDay}</span>
        <span style={{ color: "var(--text-muted)" }}>{record.dateLabel}</span>
      </span>
    ),
  });

  return {
    columns: result,
    tableWidth,
    scrollX,
  };
}

function resolveFoundBlockHashVisibleChars(
  hashColumnWidth: number,
  adornmentWidth: number
): number {
  const availableHashTextWidth = Math.max(
    0,
    hashColumnWidth - FOUND_BLOCKS_HASH_CELL_PADDING_PX - adornmentWidth
  );
  const maxChars = Math.floor(
    availableHashTextWidth / FOUND_BLOCKS_HASH_CHAR_WIDTH_PX
  );
  return Math.max(
    FOUND_BLOCKS_HASH_MIN_SUFFIX_LENGTH,
    maxChars - FOUND_BLOCKS_HASH_PREFIX.length
  );
}

function renderCenteredTableHeader(label: string) {
  return <span style={{ display: "block", textAlign: "center" }}>{label}</span>;
}

function formatFoundBlockHash(hash: string, visibleChars: number): string {
  if (hash.length <= visibleChars) return hash;
  return `${FOUND_BLOCKS_HASH_PREFIX}${hash.slice(-visibleChars)}`;
}

function resolveFoundBlocksFoundMaxCharsPerLine(foundWidth: number): number {
  const availableTextWidth = Math.max(
    0,
    foundWidth - FOUND_BLOCKS_FOUND_CELL_PADDING_PX
  );
  return Math.max(
    4,
    Math.floor(availableTextWidth / FOUND_BLOCKS_FOUND_TEXT_CHAR_WIDTH_PX)
  );
}

function formatFoundBlocksFoundBy(
  value: string,
  maxCharsPerLine: number
): string[] {
  const normalizedValue = value.trim().replace(/\s+/g, " ");
  if (normalizedValue.length == 0) return [""];
  if (!normalizedValue.includes(" ")) {
    return [truncateFoundBlocksLine(normalizedValue, maxCharsPerLine)];
  }
  if (normalizedValue.length <= maxCharsPerLine) {
    return [normalizedValue];
  }

  const words = normalizedValue.split(" ");
  let bestSplitIndex = -1;
  let bestSplitScore = Number.POSITIVE_INFINITY;

  for (let index = 1; index < words.length; index += 1) {
    const firstLine = words.slice(0, index).join(" ");
    const secondLine = words.slice(index).join(" ");

    if (
      firstLine.length > maxCharsPerLine ||
      secondLine.length > maxCharsPerLine
    ) {
      continue;
    }

    const score = Math.abs(firstLine.length - secondLine.length);
    if (score < bestSplitScore) {
      bestSplitScore = score;
      bestSplitIndex = index;
    }
  }

  if (bestSplitIndex != -1) {
    return [
      words.slice(0, bestSplitIndex).join(" "),
      words.slice(bestSplitIndex).join(" "),
    ];
  }

  let firstLineWordsCount = 1;
  for (let index = 1; index < words.length; index += 1) {
    const candidate = words.slice(0, index + 1).join(" ");
    if (candidate.length > maxCharsPerLine) break;
    firstLineWordsCount = index + 1;
  }

  const firstLine = words.slice(0, firstLineWordsCount).join(" ");
  const secondLine = words.slice(firstLineWordsCount).join(" ");

  return [
    truncateFoundBlocksLine(firstLine, maxCharsPerLine),
    truncateFoundBlocksLine(secondLine, maxCharsPerLine),
  ];
}

function truncateFoundBlocksLine(value: string, maxChars: number): string {
  if (value.length <= maxChars) return value;
  if (maxChars <= 3) return value.slice(0, maxChars);
  return `${value.slice(0, maxChars - 3)}...`;
}

function resolveFoundBlocksDateColumnWidth({
  blocks,
  isMobile,
  headerLabel,
}: {
  blocks: IDomainFoundBlock[] | undefined;
  isMobile: boolean;
  headerLabel: string;
}): number {
  const maxValueLength = (blocks ?? []).reduce((maxLength, block) => {
    return Math.max(maxLength, block.timeOfDay.length, block.dateLabel.length);
  }, headerLabel.length);
  const charWidth = isMobile ? 7.6 : 8;
  const cellPadding = isMobile ? 26 : 30;

  return Math.ceil(maxValueLength * charWidth + cellPadding);
}

function resolveFoundBlocksSingleLineColumnWidth({
  blocks,
  isMobile,
  headerLabel,
  valueGetter,
  minWidth,
}: {
  blocks: IDomainFoundBlock[] | undefined;
  isMobile: boolean;
  headerLabel: string;
  valueGetter: (block: IDomainFoundBlock) => string;
  minWidth: number;
}): number {
  const maxValueLength = (blocks ?? []).reduce((maxLength, block) => {
    return Math.max(maxLength, valueGetter(block).length);
  }, headerLabel.length);
  const charWidth = isMobile ? 7.6 : 8;
  const cellPadding = isMobile ? 24 : 28;

  return Math.max(minWidth, Math.ceil(maxValueLength * charWidth + cellPadding));
}

export function provideUserWorkersDataColumns(
  intl: IntlShape,
  width: number,
  currentTime: number
): TableColumnsType<IDomainUserStatWorker> {
  const result: TableColumnsType<IDomainUserStatWorker> = [];
  const isMobile = width <= 768;

  result.push({
    title: "",
    key: "state",
    dataIndex: "key",
    width: width <= 768 ? 48 : 54,
    align: "center",
    onCell: () => ({
      style: {
        paddingLeft: 4,
        paddingRight: 4,
      },
    }),
    onHeaderCell: () => ({
      style: {
        paddingLeft: 4,
        paddingRight: 4,
      },
    }),
    render: (_, record) => provideWorkerStatusImage(record),
  });
  result.push({
    title: intl.messages["column-worker"] as string,
    dataIndex: "worker",
    key: "worker",
    ellipsis: { showTitle: false },
    sorter: (a, b) => a.worker.localeCompare(b.worker),
  });
  if (width >= 992) {
    result.push({
      title: intl.messages["column-share-rate"] as string,
      dataIndex: "sharerate",
      key: "sharerate",
      ellipsis: true,
      render: (_, record) => `${record.sharerate} /sec`,
      sorter: (a, b) => a.sharerate.localeCompare(b.sharerate),
    });
  }
  result.push({
    title: intl.messages["column-hash-rate"] as string,
    dataIndex: "hashrate",
    key: "hashrate",
    ellipsis: { showTitle: false },
    sorter: (a, b) => a.hashrate.localeCompare(b.hashrate),
  });
  result.push({
    title: intl.messages["monitoring-column-last-seen"] as string,
    dataIndex: "lastseen",
    key: "lastseen",
    ellipsis: { showTitle: false },
    render: (_, record) =>
      provideLastSeen(currentTime - record.lastseen, intl.locale),
    sorter: (a, b) => a.lastseen - b.lastseen,
  });
  if (isMobile) {
    result.push({
      title: intl.messages["column-share-rate"] as string,
      dataIndex: "sharerate",
      key: "sharerate-mobile",
      ellipsis: true,
      render: (_, record) => `${record.sharerate} /sec`,
      sorter: (a, b) => a.sharerate.localeCompare(b.sharerate),
    });
  }

  return result;
}

const provideLastSeen = (seconds: number, locale: string): string => {
  seconds = Math.floor(seconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let result = "";
  if (hours > 0) result += `${hours}${locale == "ru" ? "ч" : "h"} `;
  if (minutes > 0) result += `${minutes}${locale == "ru" ? "м" : "m"} `;
  if (remainingSeconds > 0)
    result += `${remainingSeconds}${locale == "ru" ? "с" : "s"} `;
  if (hours == 0 && minutes == 0 && seconds == 0)
    result = `0${locale == "ru" ? "с" : "s"}`;
  return result.trim();
};

export function provideConnectColumns(
  intl: IntlShape,
  width: number
): TableColumnsType<IDomainConnect> {
  const result: TableColumnsType<IDomainConnect> = [];
  if (width > 768) {
    result.push({
      key: "protocol",
      title: intl.messages["column-protocol"] as string,
      dataIndex: "protocol",
      ellipsis: { showTitle: false },
    });
  }
  if (width > 768) {
    result.push({
      key: "type",
      title: intl.messages["column-type"] as string,
      dataIndex: "type",
      ellipsis: { showTitle: false },
    });
  }
  result.push({
    key: "port",
    title: intl.messages["column-port"] as string,
    dataIndex: "port",
    ellipsis: { showTitle: false },
  });
  result.push({
    key: "backends",
    title: intl.messages["column-backends"] as string,
    dataIndex: "backends",
    ellipsis: { showTitle: false },
  });
  result.push({
    key: "difficulty",
    title: intl.messages["column-difficulty-oneword"] as string,
    dataIndex: "difficulty",
    ellipsis: { showTitle: false },
  });
  return result;
}

export function provideUserPoolHistoryColumns(
  intl: IntlShape,
  width: number
): TableColumnsType<IDomainPoolHistoryUserStat> {
  const result: TableColumnsType<IDomainPoolHistoryUserStat> = [];

  result.push({
    key: "date",
    title: intl.messages["column-date"] as string,
    dataIndex: "date",
    ellipsis: true,
  });

  if (width > 768) {
    result.push({
      key: "shareRate",
      title: intl.messages["column-share-rate"] as string,
      dataIndex: "shareRate",
      ellipsis: { showTitle: false },
    });
  }

  result.push({
    key: "hashRate",
    title: intl.messages["column-hash-rate"] as string,
    dataIndex: "hashRate",
    ellipsis: true,
  });
  result.push({
    key: "difficulty",
    title: intl.messages["column-difficulty"] as string,
    dataIndex: "difficulty",
    ellipsis: true,
  });

  return result;
}

export function provideUserPoolHistoryColumnsWithProfitability(
  intl: IntlShape,
  width: number,
  alg: string,
  coin: string,
  unit: string,
  value: string
): TableColumnsType<IDomainPoolHistoryUserStat> {
  const result: TableColumnsType<IDomainPoolHistoryUserStat> = [];
  const isMobile = width <= 768;

  result.push({
    key: "date",
    title: intl.messages["column-date"] as string,
    dataIndex: "date",
    ellipsis: {
      showTitle: false,
    },
    width: isMobile ? 18 : undefined,
  });

  if (alg == "sha256") {
    result.push({
      key: "profitabilityUSD",
      title: isMobile
        ? `USD/${value}${unit}`
        : intl.formatMessage(
            { id: "column-profitability-usd" },
            { value: value, unit: unit }
          ),
      dataIndex: "profitabilityUSD",
      ellipsis: {
        showTitle: false,
      },
      width: isMobile ? 15 : undefined,
    });

    if (width > 768) {
      result.push({
        key: "profitabilityCoin",
        title: intl.formatMessage(
          { id: "column-profitability-coin" },
          { coin: coin, value: value, unit: unit }
        ),
        dataIndex: "profitabilityCoin",
        ellipsis: {
          showTitle: false,
        },
      });
    }
  } else {
    if (width > 768) {
      result.push({
        key: "shareRate",
        title: intl.messages["column-share-rate"] as string,
        dataIndex: "shareRate",
        ellipsis: {
          showTitle: false,
        },
      });
    }
  }

  if (width > 768) {
    result.push({
      key: "hashRate",
      title: intl.messages["column-hash-rate"] as string,
      dataIndex: "hashRate",
      ellipsis: {
        showTitle: false,
      },
    });
    result.push({
      key: "difficulty",
      title: intl.messages["column-difficulty"] as string,
      dataIndex: "difficulty",
      ellipsis: {
        showTitle: false,
      },
    });
  } else {
    result.push({
      key: "hashRate",
      title: "Hash",
      dataIndex: "hashRate",
      ellipsis: {
        showTitle: false,
      },
      width: 17,
    });
  }

  return result;
}

export function providePoolHistoryColumns(
  intl: IntlShape,
  width: number
): TableColumnsType<IDomainPoolHistoryStat> {
  const result: TableColumnsType<IDomainPoolHistoryStat> = [];
  result.push({
    key: "date",
    title: <div style={{ textAlign: "center" }}>{intl.messages["column-date"] as string}</div>,
    dataIndex: "date",
    ellipsis: true,
    align: "center",
  });
  result.push({
    key: "hashRate",
    title: <div style={{ textAlign: "left" }}>{intl.messages["column-hash-rate"] as string}</div>,
    dataIndex: "hashRate",
    ellipsis: true,
  });
  result.push({
    key: "difficulty",
    title: <div style={{ textAlign: "left" }}>{intl.messages["column-difficulty"] as string}</div>,
    dataIndex: "difficulty",
    ellipsis: true,
    width: width > 1120 ? 148 : 132,
  });
  if (width > 768) {
    result.push({
      key: "shareRate",
      title: <div style={{ textAlign: "left" }}>{intl.messages["column-share-rate"] as string}</div>,
      dataIndex: "shareRate",
      ellipsis: { showTitle: false },
    });
  }
  return result;
}

// export function provideUsersSelectorOptions(
//   users: Map<string, IUserData> | undefined
// ): BaseOptionType[] {
//   if (!users) return [];
//   const result: BaseOptionType[] = [];
//   users.forEach((u) => {
//     result.push({
//       value: u.login,
//       label: u.login,
//     });
//   });
//   return result;
// }

export function provideUsersSelectorOptions(
  users: Map<string, IUserData> | undefined
): BaseOptionType[] {
  if (!users) return [];

  const result: BaseOptionType[] = [];

  for (const [, u] of users) {
    result.push({
      value: u.login,
      label: u.login,
    });
  }

  return result;
}

// export function provideUsersSelectorOptionsAsMap(
//   users: Map<string, IUserData> | undefined
// ): Map<string, BaseOptionType> {
//   if (!users) return new Map();
//   const result: Map<string, BaseOptionType> = new Map();
//   users.values().forEach((u) => {
//     result.set(u.login!, {
//       value: u.login,
//       label: u.login,
//     });
//   });
//   return result;
// }

export function provideUsersSelectorOptionsAsMap(
  users: Map<string, IUserData> | undefined
): Map<string, BaseOptionType> {
  if (!users) return new Map();

  const result: Map<string, BaseOptionType> = new Map();

  for (const [, u] of users) {
    result.set(u.login!, {
      value: u.login,
      label: u.login,
    });
  }

  return result;
}


export function providePayoutsColumns(
  intl: IntlShape,
  screenWidth: number
): TableColumnsType<IDomainPayout> {
  const result: TableColumnsType<IDomainPayout> = [];
  const isMobile = screenWidth <= 768;
  const sliseStart = isMobile ? 3 : 4;
  const sliseEnd = isMobile ? 4 : 6;

  result.push({
    title: intl.messages["column-date"] as string,
    key: "date",
    dataIndex: "date",
    render: (dateNumber) => (
      <>{screenWidth > 1120 ? formatUnixDateTimeEnglish(dateNumber) : formatUnixDateEnglish(dateNumber)}</>
    ),
  });
  result.push({
    title: "TxId",
    key: "txid",
    dataIndex: "txid",
    render:
      screenWidth < 1120
        ? (text) => <>{`${(text as unknown as string).slice(0, sliseStart)}...${(text as unknown as string).slice(-sliseEnd)}`}</>
        : undefined,
  });
  result.push({
    title: intl.messages["column-amount"] as string,
    key: "amount",
    dataIndex: "amount",
    render: (value) =>
      isMobile
        ? provideMobilePayoutAmount(String(value))
        : String(value),
    ellipsis: {
      showTitle: false,
    },
  });

  return result;
}

function provideMobilePayoutAmount(value: string): string {
  const normalized = value.replace(/\s+/g, "");
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return value;

  const [integerPartRaw, fractionalPartRaw] = normalized.split(".");
  const integerPart = integerPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  if (Math.abs(amount) >= 1000) return integerPart;
  if (Math.abs(amount) > 1) {
    const trimmedFraction = (fractionalPartRaw ?? "").slice(0, 3);
    return trimmedFraction.length > 0
      ? `${integerPart}.${trimmedFraction}`
      : integerPart;
  }

  return value;
}

export function provideUsersTableColumns(
  intl: IntlShape,
  options: {
    hideFeePlan?: boolean;
    hideReadOnly?: boolean;
  } = {}
): TableColumnsType<IDomainUser> {
  const { hideFeePlan = false, hideReadOnly = false } = options;
  const currentTime = getCurrentTimeTimestamp();

  const provideLastSeen = (lastSeen: number): string => {
    if (currentTime - lastSeen > 518_400) return provideLastSeenDate(lastSeen);
    if (currentTime - lastSeen > 432_900)
      return intl.messages["last-seen-days-5"] as string;
    if (currentTime - lastSeen > 86_400)
      return intl.messages["last-seen-days-1"] as string;
    else if (currentTime - lastSeen > 36_000)
      return intl.messages["last-seen-hours-10"] as string;
    else if (currentTime - lastSeen > 18_000)
      return intl.messages["last-seen-hours-5"] as string;
    else if (currentTime - lastSeen > 3_600)
      return intl.messages["last-seen-hours-1"] as string;
    else if (currentTime - lastSeen > 1_800)
      return intl.messages["last-seen-minutes-30"] as string;
    else if (currentTime - lastSeen > 900)
      return intl.messages["last-seen-minutes-15"] as string;
    else if (currentTime - lastSeen > 600)
      return intl.messages["last-seen-minutes-10"] as string;
    else if (currentTime - lastSeen > 300)
      return intl.messages["last-seen-minutes-5"] as string;
    else if (currentTime - lastSeen > 240)
      return intl.messages["last-seen-minutes-4"] as string;
    else if (currentTime - lastSeen > 180)
      return intl.messages["last-seen-minutes-3"] as string;
    else if (currentTime - lastSeen > 120)
      return intl.messages["last-seen-minutes-2"] as string;
    else if (currentTime - lastSeen > 60)
      return intl.messages["last-seen-minutes-1"] as string;
    return intl.messages["last-seen-less-1-min"] as string;
  };

  const provideLastSeenDate = (regDate: number): string => {
    if (regDate == 0) return intl.messages["last-seen-no-data"] as string;
    return formatUnixDateEnglish(regDate);
  };

  const provideRegDate = (regDate: number): string => {
    if (regDate == 0) return "";
    return formatUnixDateTimeEnglish(regDate);
  };

  const result: TableColumnsType<IDomainUser> = [];
  const readOnlyYes = intl.messages["column-readonly-true"] as string;
  const readOnlyNo = intl.messages["column-readonly-false"] as string;

  result.push({
    title: intl.messages["column-login"] as string,
    dataIndex: "loginAndName",
    key: "loginAndName",
    sorter: (a, b) => compareNaturalText(a.login, b.login),
    ellipsis: { showTitle: false },
  });
  result.push({
    title: intl.messages["column-email"] as string,
    dataIndex: "email",
    key: "email",
    sorter: (a, b) => a.email.localeCompare(b.email),
    ellipsis: { showTitle: false },
  });
  result.push({
    title: intl.messages["column-reg-date"] as string,
    dataIndex: "registrationDate",
    key: "registrationDate",
    sorter: (a, b) => a.registrationDate - b.registrationDate,
    render: (_, record) => provideRegDate(record.registrationDate),
    ellipsis: { showTitle: false },
  });
  if (!hideFeePlan) {
    result.push({
      title: intl.messages["column-fee-plan"] as string,
      dataIndex: "feePlanId",
      key: "feePlanId",
      sorter: (a, b) => provideSorterForUsers(a.feePlanId, b.feePlanId),
      ellipsis: { showTitle: false },
    });
  }
  result.push({
    title: intl.messages["column-power"] as string,
    dataIndex: "power",
    key: "power",
    ellipsis: { showTitle: false },
    render: (_, record) => provideUserPower(record.powerSimple, record.shareRate),
    sorter: (a, b) => a.power - b.power,
  });
  result.push({
    title: intl.messages["column-last-seen"] as string,
    dataIndex: "lastShareTime",
    key: "lastShareTime",
    sorter: (a, b) => a.lastSeen - b.lastSeen,
    render: (_, record) => provideLastSeen(record.lastSeen),
    ellipsis: { showTitle: false },
  });
  if (!hideReadOnly) {
    result.push({
      title: intl.messages["column-readonly"] as string,
      dataIndex: "isReadOnly",
      key: "isReadOnly",
      sorter: (a, b) => Number(a.isReadOnly) - Number(b.isReadOnly),
      render: (_, record) => (record.isReadOnly ? readOnlyYes : readOnlyNo),
      ellipsis: { showTitle: false },
    });
  }

  return result;
}

export function provideTableColumns(
  intl: IntlShape
): TableColumnsType<UserWithPercentageWithKey> {
  const result: TableColumnsType<UserWithPercentageWithKey> = [];

  result.push({
    title: intl.messages["title-percentage"] as string,
    dataIndex: "percentage",
    key: "percentage",
  });
  result.push({
    title: intl.messages["title-user-login"] as string,
    dataIndex: "userId",
    key: "userId",
    width: "100%",
  });

  return result;
}

function provideWorkerStatusImage(record: IDomainUserStatWorker) {
  let statusIcon = undefined;
  if (record.workerState == "Normal")
    statusIcon = <CheckCircleOutlined style={{ color: "#37f337" }} />;
  else if (record.workerState == "Warning")
    statusIcon = <ExclamationCircleOutlined style={{ color: "orange" }} />;
  else if (record.workerState == "Error")
    statusIcon = <MinusCircleOutlined style={{ color: "red" }} />;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        lineHeight: 1,
      }}
    >
      {statusIcon}
      <LineChartOutlined style={{ color: "#5B8FF9", fontSize: 11 }} />
    </span>
  );
}

function provideSorterForUsers(
  a: string | undefined,
  b: string | undefined
): number {
  if (a && b) {
    return compareNaturalText(a, b);
  } else if (a && !b) return 1;
  else if (!a && b) return -1;
  else return 0;
}

function compareNaturalText(first: string, second: string): number {
  return naturalTextCollator.compare(first, second);
}

function provideUserPower(power: string, shareRate: number): string {
  return `${power} / ${formatShareRateValue(shareRate)}`;
}
